import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import StatusChip from "@/components/StatusChip";
import { ArrowLeft, Check, X, RotateCcw, Save, Plus, Trash2, Mail, Paperclip, Calendar, Send, GitCommit, History, Layers, PenSquare, Upload, Download, ShieldAlert, FileDown, Clock, ChevronDown, ChevronRight, GitCompare } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

// Format an ISO timestamp as "DD Mon YYYY · HH:MM IST"
function fmtIst(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString("en-GB", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false }).replace(",", " ·") + " IST";
}

function LastSavedChip({ at, by, label = "Last saved", testid }) {
  const when = fmtIst(at);
  if (!when) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-slate-200 bg-slate-50 text-[10.5px] font-mono text-slate-400" data-testid={testid}>
        <Clock className="h-3 w-3" /> {label}: —
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-emerald-200 bg-emerald-50 text-[10.5px] font-mono text-emerald-800" data-testid={testid} title={by ? `by ${by}` : undefined}>
      <Clock className="h-3 w-3" /> {label}: {when}{by ? <span className="text-emerald-700/70">· by {by}</span> : null}
    </span>
  );
}

function TimelineStamp({ label, at, by, testid }) {
  const when = fmtIst(at);
  const empty = !when;
  return (
    <div
      data-testid={testid}
      className={`rounded-md border p-2.5 flex items-start gap-2 ${empty ? "border-slate-200 bg-slate-50/60" : "border-emerald-200 bg-emerald-50/60"}`}
    >
      <Clock className={`h-4 w-4 mt-0.5 shrink-0 ${empty ? "text-slate-400" : "text-emerald-700"}`} />
      <div className="min-w-0">
        <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{label} last saved</div>
        <div className={`text-[12px] font-semibold mt-0.5 ${empty ? "text-slate-400" : "text-slate-800"}`}>{when || "—"}</div>
        {by && !empty && <div className="text-[11px] text-slate-500 font-mono truncate">by {by}</div>}
      </div>
    </div>
  );
}

const EMPTY_FABRIC = { type: "", position: "", fabric_type: "Woven", width: "", uom: "Meters", consumption: "", remark: "" };
const EMPTY_TAPE = { item: "", uom: "Meters", width: "", consumption: "", remark: "" };
const EMPTY_THREAD = { color: "", tex_details: "", consumption_m: "" };
const EMPTY_PRODUCTION = {
  labour_rate: 0, bartack: 0, button_hole: 0, eye_hole: 0, button_stitch: 0, snap: 0, folding_ironing: 0, any_other_work: "",
  sewing_threads: [], embroidery_threads: [], data_based_on: [],
};
const DATA_BASED_ON_OPTIONS = ["Physical Sample", "TechPack", "Sample Photos"];
const LABOUR_FIELDS = [
  ["labour_rate", "Labour Rate", "number"],
  ["bartack", "Bartack (No. of Points)", "number"],
  ["button_hole", "Button Hole (No. of Points)", "number"],
  ["eye_hole", "Eye Hole (No. of Points)", "number"],
  ["button_stitch", "Button Stitch (No. of Points)", "number"],
  ["snap", "Snap / Riveting (No. of Points)", "number"],
  ["folding_ironing", "Folding & Ironing (No. of Points)", "number"],
  ["any_other_work", "Any Other Work", "text"],
];

export default function EnquiryDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [e, setE] = useState(null);
  const [fabrics, setFabrics] = useState([]);
  const [tapes, setTapes] = useState([]);
  const [production, setProduction] = useState(EMPTY_PRODUCTION);
  const [lookups, setLookups] = useState(null);
  const [saving, setSaving] = useState(false);
  const [approvalComment, setApprovalComment] = useState("");
  const [approvalOpen, setApprovalOpen] = useState(false);
  const [approvalAction, setApprovalAction] = useState("approve");
  const [newVersionOpen, setNewVersionOpen] = useState(false);
  const [newVersionNote, setNewVersionNote] = useState("");
  const [compareOpen, setCompareOpen] = useState(false);
  const [compareA, setCompareA] = useState(null);
  const [compareB, setCompareB] = useState(null);
  const [expandedVersion, setExpandedVersion] = useState(null);      // int | null — which version's diff row is open
  const [diffAgainst, setDiffAgainst] = useState("current");        // "current" | "previous"
  // Revise flow
  const [reviseOpen, setReviseOpen] = useState(false);
  const [reviseReason, setReviseReason] = useState("");
  const [reviseBusy, setReviseBusy] = useState(false);
  const [revNotifs, setRevNotifs] = useState([]);
  // Attachments
  const [attBusy, setAttBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/enquiries/${id}`);
    setE(data);
    setFabrics(data.fabrics || []);
    setTapes(data.tapes_elastic || []);
    setProduction({ ...EMPTY_PRODUCTION, ...(data.production_data || {}) });
    try {
      const { data: rn } = await api.get(`/enquiries/${id}/revision-notifications`);
      setRevNotifs(rn || []);
    } catch { /* ignore */ }
  };
  const loadLookups = async () => {
    try { const { data } = await api.get("/masters/enquiry-lookups"); setLookups(data); } catch {}
  };
  useEffect(() => { load(); loadLookups(); /* eslint-disable-next-line */ }, [id]);

  if (!e) return <div className="text-slate-500 text-sm">Loading…</div>;

  const isManagement = (user?.role === "admin" || user?.role === "admin_addl") || user?.role === "management";
  const canRevise = ["admin", "cad", "merchandiser", "production"].includes(user?.role);
  const canManageAttachments = (user?.role === "admin" || user?.role === "admin_addl") || user?.role === "merchandiser";
  const canEdit = !["Approved", "Rejected"].includes(e.status);
  const isCad = user?.role === "cad";
  const isProd = user?.role === "production";
  const isMerch = user?.role === "merchandiser";
  // Role-scoped edit gates — mirror the backend `edit_cad_section` / `edit_prod_section` capabilities.
  // Admin always passes; other roles are checked against the capability list issued at login.
  const caps = user?.capabilities || [];
  const hasCap = (c) => (user?.role === "admin" || user?.role === "admin_addl") || caps.includes(c);
  const canEditCad = canEdit && hasCap("edit_cad_section");
  const canEditProd = canEdit && hasCap("edit_prod_section");
  const canSeeProdData = !isCad; // CAD cannot see Production Data tab or its columns in Compare
  // A revised/send-back enquiry keeps last version's sent_for_approval_at stamp while
  // its status went back to Pending — treat those as "not sent" so the Submit buttons
  // re-appear (backend clears the stale stamps on the first post-revise submit).
  const WORKFLOW_RESTARTED = ["Pending CAD Input", "Pending Production Input", "Ready for Approval"].includes(e.status);
  const sentForApproval = !!e.sent_for_approval_at && !WORKFLOW_RESTARTED;
  const cadSubmitted = !!e.cad_submitted_at;
  const prodSubmitted = !!e.production_submitted_at;

  // Role-scoped submit
  const submitCad = async () => {
    try {
      await api.post(`/enquiries/${id}/cad/submit`, { fabrics, tapes_elastic: tapes });
      toast.success("CAD data submitted");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Submit failed"); }
  };
  const submitProduction = async () => {
    try {
      await api.post(`/enquiries/${id}/production/submit`, { production_data: production });
      toast.success("Production data submitted");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Submit failed"); }
  };

  // Role-scoped PDF export
  const pdfScope = user?.role === "cad" ? "cad" : user?.role === "production" ? "production" : "full";
  const pdfLabel = pdfScope === "cad" ? "Download CAD PDF" : pdfScope === "production" ? "Download Production PDF" : "Download Full PDF";
  const downloadPdf = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/enquiries/${id}/export/pdf?scope=${pdfScope}`, { credentials: "include" });
      if (!res.ok) throw new Error(await res.text());
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `Enquiry_${e.enquiry_number}_${pdfScope}.pdf`; a.click();
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch (err) {
      toast.error("PDF export failed");
    }
  };

  // ---------- Row mutators ----------
  const patchFabric = (i, patch) => setFabrics((rows) => rows.map((r, idx) => {
    if (idx !== i) return r;
    const next = { ...r, ...patch };
    if (next.fabric_type === "Woven") next.uom = "Meters"; // Woven always Meters
    return next;
  }));
  const patchTape = (i, patch) => setTapes((rows) => rows.map((r, idx) => idx === i ? { ...r, ...patch } : r));
  const patchThread = (kind, i, patch) => setProduction((p) => {
    const list = [...(p[kind] || [])];
    list[i] = { ...list[i], ...patch };
    return { ...p, [kind]: list };
  });

  const save = async () => {
    setSaving(true);
    try {
      // Only send the section(s) the current user is allowed to edit — otherwise the backend
      // will 403 the entire request because it does a field-level capability check.
      const payload = {};
      if (canEditCad) { payload.fabrics = fabrics; payload.tapes_elastic = tapes; }
      if (canEditProd) { payload.production_data = production; }
      if (Object.keys(payload).length === 0) {
        toast.error("You don't have permission to edit any section of this enquiry");
        setSaving(false); return;
      }
      await api.patch(`/enquiries/${id}`, payload);
      toast.success("Costing saved");
      load();
    } catch { toast.error("Save failed"); }
    setSaving(false);
  };
  const submit = async () => {
    await save();
    await api.post(`/enquiries/${id}/submit`);
    toast.success("Submitted for review");
    load();
  };
  const doApproval = async () => {
    try {
      await api.post(`/enquiries/${id}/approve`, { action: approvalAction, comment: approvalComment });
      toast.success(`Enquiry ${approvalAction}d`);
      setApprovalOpen(false); setApprovalComment(""); load();
    } catch { toast.error("Approval failed"); }
  };
  const doNewVersion = async () => {
    try {
      await save();
      await api.post(`/enquiries/${id}/new_version`, { note: newVersionNote });
      toast.success("New version snapshot created");
      setNewVersionOpen(false); setNewVersionNote(""); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Snapshot failed"); }
  };
  const restoreVersion = async (v) => {
    if (!window.confirm(`Restore version v${v}? A safety snapshot of the current state is auto-saved first.`)) return;
    try {
      const { data } = await api.post(`/enquiries/${id}/restore_version/${v}`);
      toast.success(`Restored v${v} as new v${data.version}`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Restore failed"); }
  };

  const doRevise = async () => {
    if (!reviseReason.trim()) { toast.error("Please provide a reason for the revision"); return; }
    setReviseBusy(true);
    try {
      // Save current edits first so the snapshot is the latest state — but only send sections the user owns
      const revisePayload = {};
      if (canEditCad) { revisePayload.fabrics = fabrics; revisePayload.tapes_elastic = tapes; }
      if (canEditProd) { revisePayload.production_data = production; }
      if (Object.keys(revisePayload).length > 0) {
        await api.patch(`/enquiries/${id}`, revisePayload);
      }
      const { data } = await api.post(`/enquiries/${id}/revise`, { reason: reviseReason });
      toast.success("Revision recorded", { description: `Notified ${data.notification.recipients.length} stakeholder(s) · v${data.notification.from_version} → v${data.notification.to_version}` });
      setReviseOpen(false); setReviseReason(""); load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Revise failed");
    }
    setReviseBusy(false);
  };

  const uploadAttachment = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("File exceeds 5 MB limit"); return; }
    setAttBusy(true);
    try {
      const b64 = await new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = () => res(String(r.result).split(",").pop());
        r.onerror = () => rej(r.error);
        r.readAsDataURL(file);
      });
      await api.post(`/enquiries/${id}/attachments`, { name: file.name, mime: file.type || "application/octet-stream", data_base64: b64 });
      toast.success(`Uploaded ${file.name}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    }
    setAttBusy(false);
  };

  const deleteAttachment = async (aid) => {
    if (!window.confirm("Delete this attachment?")) return;
    try {
      await api.delete(`/enquiries/${id}/attachments/${aid}`);
      toast.success("Deleted");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Delete failed"); }
  };

  const versions = e.versions || [];
  const openCompare = () => {
    // Latest snapshot (or current live) vs previous
    const list = [...versions].sort((a, b) => (b.version || 0) - (a.version || 0));
    const liveSnap = { version: e.version, note: "Current (live)", fabrics, tapes_elastic: tapes, production_data: production, created_by: e.updated_by || "", created_at: e.updated_at || "" };
    setCompareA(liveSnap);
    setCompareB(list[0] || null);
    setCompareOpen(true);
  };

  return (
    <div className="space-y-6" data-testid="enquiry-detail-page">
      {/* Header */}
      <div>
        <button onClick={() => nav("/enquiries")} className="inline-flex items-center gap-1.5 text-[12.5px] text-slate-500 hover:text-slate-800 mb-3">
          <ArrowLeft className="h-3.5 w-3.5" /> Back to Enquiries
        </button>
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="font-heading font-bold text-3xl tracking-tight">{e.enquiry_number}</h1>
              <span className="px-2 py-0.5 rounded bg-slate-100 text-slate-700 text-[12px] font-mono">v{e.version || 1}</span>
              <StatusChip status={e.status} />
            </div>
            <div className="mt-2 text-slate-600 text-[14px]">{e.buyer} · <span className="text-slate-900 font-semibold">{e.buyer_style}</span></div>
            <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-1 text-[12.5px] text-slate-500">
              <span className="inline-flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" /> {e.source_email}</span>
              <span className="inline-flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" /> {e.received_date}</span>
              <span className="inline-flex items-center gap-1.5"><Paperclip className="h-3.5 w-3.5" /> {e.attachments?.length || 0} attachments</span>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" onClick={downloadPdf} data-testid="enquiry-pdf-btn">
              <FileDown className="h-4 w-4 mr-2" /> {pdfLabel}
            </Button>
            {canEdit && (
              <>
                <Button variant="outline" onClick={save} disabled={saving} data-testid="enquiry-save-btn"><Save className="h-4 w-4 mr-2" /> Save</Button>
                <Button variant="outline" onClick={() => setNewVersionOpen(true)} data-testid="enquiry-new-version-btn"><GitCommit className="h-4 w-4 mr-2" /> New Version</Button>
              </>
            )}
            {canRevise && (
              <Button variant="outline" onClick={() => { setReviseReason(""); setReviseOpen(true); }} className="border-amber-300 text-amber-800 hover:bg-amber-50" data-testid="enquiry-revise-btn">
                <PenSquare className="h-4 w-4 mr-2" /> Revise Details
              </Button>
            )}
            {canEdit && !sentForApproval && (isCad || ((user?.role === "admin" || user?.role === "admin_addl") && !cadSubmitted)) && (
              <Button onClick={submitCad} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="cad-submit-btn">
                <Check className="h-4 w-4 mr-2" /> {cadSubmitted ? "Re-submit CAD Data" : "Submit CAD Data"}
              </Button>
            )}
            {canEdit && !sentForApproval && (isProd || ((user?.role === "admin" || user?.role === "admin_addl") && !prodSubmitted)) && (
              <Button onClick={submitProduction} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="prod-submit-btn">
                <Check className="h-4 w-4 mr-2" /> {prodSubmitted ? "Re-submit Production Data" : "Submit Production Data"}
              </Button>
            )}
            {canEdit && (isMerch || (user?.role === "admin" || user?.role === "admin_addl")) && !sentForApproval && e.status !== "Ready for Review" && (
              <Button onClick={submit} className="bg-brand hover:bg-brand-dark text-white" data-testid="enquiry-submit-btn"><Send className="h-4 w-4 mr-2" /> Send for Approval</Button>
            )}
            {isManagement && e.status === "Ready for Review" && (
              <Dialog open={approvalOpen} onOpenChange={setApprovalOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="enquiry-review-btn"><Check className="h-4 w-4 mr-2" /> Approve / Reject</Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Review Enquiry {e.enquiry_number}</DialogTitle></DialogHeader>
                  <div className="space-y-4">
                    <div className="grid grid-cols-3 gap-2">
                      <button onClick={() => setApprovalAction("approve")} className={`p-2.5 rounded-md text-[12.5px] font-medium border ${approvalAction === "approve" ? "bg-emerald-50 border-emerald-300 text-emerald-800" : "border-slate-200"}`}><Check className="h-4 w-4 inline mr-1" /> Approve</button>
                      <button onClick={() => setApprovalAction("send_back")} className={`p-2.5 rounded-md text-[12.5px] font-medium border ${approvalAction === "send_back" ? "bg-amber-50 border-amber-300 text-amber-800" : "border-slate-200"}`}><RotateCcw className="h-4 w-4 inline mr-1" /> Send back</button>
                      <button onClick={() => setApprovalAction("reject")} className={`p-2.5 rounded-md text-[12.5px] font-medium border ${approvalAction === "reject" ? "bg-red-50 border-red-300 text-red-800" : "border-slate-200"}`}><X className="h-4 w-4 inline mr-1" /> Reject</button>
                    </div>
                    <Textarea placeholder="Add a comment…" value={approvalComment} onChange={(ev) => setApprovalComment(ev.target.value)} rows={3} data-testid="approval-comment" />
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setApprovalOpen(false)}>Cancel</Button>
                    <Button onClick={doApproval} data-testid="approval-confirm-btn" className="bg-brand hover:bg-brand-dark text-white">Confirm</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>
      </div>

      {/* Workflow timeline strip — three "last saved" moments at a glance.
          Visibility rule per user's brief:
          - CAD sees ONLY the CAD stamp
          - Production sees ONLY the Production stamp
          - Admin / Merchandiser / Management see all three */}
      {(() => {
        const showCad  = isCad  || isMerch || (user?.role === "admin" || user?.role === "admin_addl") || user?.role === "management";
        const showProd = isProd || isMerch || (user?.role === "admin" || user?.role === "admin_addl") || user?.role === "management";
        const showMerch = isMerch || (user?.role === "admin" || user?.role === "admin_addl") || user?.role === "management";
        const visibleCount = [showCad, showProd, showMerch].filter(Boolean).length;
        const cols = visibleCount === 3 ? "md:grid-cols-3" : visibleCount === 2 ? "md:grid-cols-2" : "md:grid-cols-1";
        return (
          <div className={`grid grid-cols-1 ${cols} gap-2`} data-testid="enquiry-timeline-strip">
            {showCad   && <TimelineStamp label="CAD section" at={e.cad_last_saved_at} by={e.cad_last_saved_by} testid="timeline-cad" />}
            {showProd  && <TimelineStamp label="Production section" at={e.prod_last_saved_at} by={e.prod_last_saved_by} testid="timeline-prod" />}
            {showMerch && <TimelineStamp label="Sent for approval" at={e.merch_last_saved_at || e.sent_for_approval_at} by={e.merch_last_saved_by || e.sent_for_approval_by} testid="timeline-merch" />}
          </div>
        );
      })()}

      {/* Tabs */}
      <Tabs defaultValue="fabric" className="space-y-4">
        <TabsList data-testid="enquiry-tabs">
          <TabsTrigger value="fabric" data-testid="tab-fabric">Fabric & Tapes/Elastic</TabsTrigger>
          {canSeeProdData && <TabsTrigger value="production" data-testid="tab-production">Production Data</TabsTrigger>}
          <TabsTrigger value="attachments" data-testid="tab-attachments">
            Attachments
            <span className="ml-1.5 text-[10.5px] font-mono px-1.5 py-0.5 rounded bg-slate-100 text-slate-700">{(e.attachments || []).length}</span>
          </TabsTrigger>
          <TabsTrigger value="versions" data-testid="tab-versions">Version History</TabsTrigger>
          <TabsTrigger value="activity" data-testid="tab-activity">Activity Log</TabsTrigger>
        </TabsList>

        {/* ------------- Fabric + Tapes/Elastic ------------- */}
        <TabsContent value="fabric" className="space-y-4">
          {/* Fabric */}
          <Card className="border-slate-200 rounded-md bg-white overflow-hidden">
            <div className="p-4 flex items-center justify-between border-b border-slate-100 gap-3 flex-wrap">
              <div>
                <div className="font-heading font-semibold text-[15px]">Fabric</div>
                <div className="text-[12px] text-slate-500">Add as many fabric lines as needed. Widths/types are managed under Masters.</div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {!isProd && <LastSavedChip at={e.cad_last_saved_at} by={e.cad_last_saved_by} label="CAD last saved" testid="cad-last-saved-chip" />}
                {canEditCad && (
                  <Button size="sm" variant="outline" onClick={() => setFabrics([...fabrics, { ...EMPTY_FABRIC }])} data-testid="add-fabric-btn">
                    <Plus className="h-4 w-4 mr-1" /> Add fabric
                  </Button>
                )}
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="opsiq-table">
                <thead>
                  <tr>
                    <th style={{ width: 160 }}>Type</th>
                    <th style={{ width: 130 }}>Position</th>
                    <th style={{ width: 130 }}>Fabric Type</th>
                    <th style={{ width: 120 }}>Width</th>
                    <th style={{ width: 110 }}>UOM</th>
                    <th style={{ width: 120 }}>Consumption</th>
                    <th>Remark</th>
                    <th style={{ width: 40 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {fabrics.map((f, i) => (
                    <tr key={i}>
                      <td>
                        <Select disabled={!canEditCad} value={f.type} onChange={(v) => patchFabric(i, { type: v })} options={lookups?.fabric_types || []} testid={`fab-type-${i}`} placeholder="Select…" />
                      </td>
                      <td><Cell disabled={!canEditCad} value={f.position} onChange={(v) => patchFabric(i, { position: v })} testid={`fab-position-${i}`} /></td>
                      <td>
                        <Select disabled={!canEditCad} value={f.fabric_type} onChange={(v) => patchFabric(i, { fabric_type: v })} options={["Woven", "Knitted"]} testid={`fab-fabrictype-${i}`} />
                      </td>
                      <td>
                        <Select disabled={!canEditCad} value={f.width} onChange={(v) => patchFabric(i, { width: v })} options={lookups?.fabric_widths || []} testid={`fab-width-${i}`} placeholder="Select…" />
                      </td>
                      <td>
                        {f.fabric_type === "Woven" ? (
                          <div className="text-[13px] font-mono text-slate-700 px-2 py-1.5 bg-slate-50 rounded">Meters</div>
                        ) : (
                          <Select disabled={!canEditCad} value={f.uom || "Meters"} onChange={(v) => patchFabric(i, { uom: v })} options={["Meters", "Kgs"]} testid={`fab-uom-${i}`} />
                        )}
                      </td>
                      <td><Cell disabled={!canEditCad} value={f.consumption} onChange={(v) => patchFabric(i, { consumption: v })} testid={`fab-consumption-${i}`} placeholder="e.g. 2.5" /></td>
                      <td><Cell disabled={!canEditCad} value={f.remark} onChange={(v) => patchFabric(i, { remark: v })} testid={`fab-remark-${i}`} placeholder="Optional" /></td>
                      <td>
                        {canEditCad && <button onClick={() => setFabrics(fabrics.filter((_, idx) => idx !== i))} className="text-slate-400 hover:text-red-600" data-testid={`fab-del-${i}`}><Trash2 className="h-4 w-4" /></button>}
                      </td>
                    </tr>
                  ))}
                  {fabrics.length === 0 && <tr><td colSpan={8} className="text-center py-8 text-slate-500">No fabric rows yet.</td></tr>}
                </tbody>
              </table>
            </div>
          </Card>

          {/* Tapes & Elastic */}
          <Card className="border-slate-200 rounded-md bg-white overflow-hidden">
            <div className="p-4 flex items-center justify-between border-b border-slate-100">
              <div>
                <div className="font-heading font-semibold text-[15px]">Tapes & Elastic</div>
                <div className="text-[12px] text-slate-500">Reflective tapes, elastic, webbing tape, piping. Items and widths managed under Masters.</div>
              </div>
              {canEditCad && (
                <Button size="sm" variant="outline" onClick={() => setTapes([...tapes, { ...EMPTY_TAPE }])} data-testid="add-tape-btn">
                  <Plus className="h-4 w-4 mr-1" /> Add row
                </Button>
              )}
            </div>
            <div className="overflow-x-auto">
              <table className="opsiq-table">
                <thead>
                  <tr>
                    <th style={{ width: 200 }}>Item</th>
                    <th style={{ width: 110 }}>UOM</th>
                    <th style={{ width: 130 }}>Width</th>
                    <th style={{ width: 150 }}>Consumption (m)</th>
                    <th>Remark</th>
                    <th style={{ width: 40 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {tapes.map((t, i) => (
                    <tr key={i}>
                      <td>
                        <Select disabled={!canEditCad} value={t.item} onChange={(v) => patchTape(i, { item: v })} options={lookups?.tape_items || []} testid={`tape-item-${i}`} placeholder="Select…" />
                      </td>
                      <td><div className="text-[13px] font-mono text-slate-700 px-2 py-1.5 bg-slate-50 rounded">Meters</div></td>
                      <td>
                        <Select disabled={!canEditCad} value={t.width} onChange={(v) => patchTape(i, { width: v })} options={lookups?.tape_widths || []} testid={`tape-width-${i}`} placeholder="Select…" />
                      </td>
                      <td><Cell disabled={!canEditCad} value={t.consumption} onChange={(v) => patchTape(i, { consumption: v })} testid={`tape-consumption-${i}`} placeholder="e.g. 1.2" /></td>
                      <td><Cell disabled={!canEditCad} value={t.remark} onChange={(v) => patchTape(i, { remark: v })} testid={`tape-remark-${i}`} placeholder="Optional" /></td>
                      <td>
                        {canEditCad && <button onClick={() => setTapes(tapes.filter((_, idx) => idx !== i))} className="text-slate-400 hover:text-red-600" data-testid={`tape-del-${i}`}><Trash2 className="h-4 w-4" /></button>}
                      </td>
                    </tr>
                  ))}
                  {tapes.length === 0 && <tr><td colSpan={6} className="text-center py-8 text-slate-500">No rows yet.</td></tr>}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>

        {/* ------------- Production Data ------------- */}
        {canSeeProdData && (
        <TabsContent value="production" className="space-y-4">
          {/* Sewing & Embroidery Thread */}
          <Card className="border-slate-200 rounded-md bg-white overflow-hidden">
            <div className="p-4 border-b border-slate-100 flex items-center justify-between gap-3 flex-wrap">
              <div>
                <div className="font-heading font-semibold text-[15px]">Sewing & Embroidery Thread</div>
                <div className="text-[12px] text-slate-500">Add multiple threads by type. Consumption is entered in meters.</div>
              </div>
              <LastSavedChip at={e.prod_last_saved_at} by={e.prod_last_saved_by} label="Production last saved" testid="prod-last-saved-chip" />
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x divide-slate-100">
              {[
                { kind: "sewing_threads", title: "Sewing Threads", accent: "text-emerald-700 bg-emerald-50", tid: "sewing" },
                { kind: "embroidery_threads", title: "Embroidery Threads", accent: "text-indigo-700 bg-indigo-50", tid: "embroidery" },
              ].map(({ kind, title, accent, tid }) => {
                const rows = production[kind] || [];
                return (
                  <div key={kind} className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[11.5px] font-mono uppercase tracking-wider ${accent}`}>{title}</div>
                      {canEditProd && (
                        <Button size="sm" variant="outline" onClick={() => setProduction((p) => ({ ...p, [kind]: [...(p[kind] || []), { ...EMPTY_THREAD }] }))} data-testid={`add-${tid}-thread`}>
                          <Plus className="h-4 w-4 mr-1" /> Add
                        </Button>
                      )}
                    </div>
                    <table className="opsiq-table">
                      <thead>
                        <tr>
                          <th>Color</th>
                          <th>Tex / Details</th>
                          <th style={{ width: 140 }}>Consumption (m)</th>
                          <th style={{ width: 32 }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((th, i) => (
                          <tr key={i}>
                            <td><Cell disabled={!canEditProd} value={th.color ?? th.item ?? ""} onChange={(v) => patchThread(kind, i, { color: v })} testid={`${tid}-color-${i}`} placeholder="e.g. White / Off-white" /></td>
                            <td><Cell disabled={!canEditProd} value={th.tex_details} onChange={(v) => patchThread(kind, i, { tex_details: v })} testid={`${tid}-tex-${i}`} placeholder="e.g. Tex 40" /></td>
                            <td><Cell disabled={!canEditProd} value={th.consumption_m} onChange={(v) => patchThread(kind, i, { consumption_m: v })} testid={`${tid}-consumption-${i}`} placeholder="e.g. 120" /></td>
                            <td>
                              {canEditProd && <button onClick={() => setProduction((p) => ({ ...p, [kind]: rows.filter((_, idx) => idx !== i) }))} className="text-slate-400 hover:text-red-600" data-testid={`${tid}-del-${i}`}><Trash2 className="h-4 w-4" /></button>}
                            </td>
                          </tr>
                        ))}
                        {rows.length === 0 && <tr><td colSpan={4} className="text-center py-6 text-slate-500 text-[13px]">No {title.toLowerCase()} yet.</td></tr>}
                      </tbody>
                    </table>
                  </div>
                );
              })}
            </div>
          </Card>

          {/* Data Based On + Labour / Finishing */}
          <Card className="p-6 border-slate-200 rounded-md bg-white">
            <div className="mb-5 pb-4 border-b border-slate-100">
              <div className="text-[11.5px] font-mono tracking-widest text-slate-500 uppercase mb-2">Data Based On</div>
              <div className="flex flex-wrap gap-2" data-testid="data-based-on-group">
                {DATA_BASED_ON_OPTIONS.map((opt) => {
                  const active = (production.data_based_on || []).includes(opt);
                  return (
                    <label key={opt} className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md border text-[13px] cursor-pointer transition-colors ${active ? "bg-brand-soft border-rose-300 text-brand font-semibold" : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"} ${!canEditProd ? "opacity-60 cursor-not-allowed" : ""}`} data-testid={`data-based-on-${opt.toLowerCase().replace(/\s+/g,'-')}`}>
                      <input
                        type="checkbox"
                        disabled={!canEditProd}
                        checked={active}
                        onChange={(ev) => {
                          const curr = production.data_based_on || [];
                          const next = ev.target.checked ? [...curr, opt] : curr.filter((x) => x !== opt);
                          setProduction((p) => ({ ...p, data_based_on: next }));
                        }}
                        className="accent-[#7C1D3A]"
                      />
                      {opt}
                    </label>
                  );
                })}
              </div>
              <div className="text-[11px] text-slate-400 mt-1.5">Multi-select — mark all sources the production data is based on.</div>
            </div>
            <div className="font-heading font-semibold text-[15px] mb-1">Labour & Finishing</div>
            <div className="text-[12px] text-slate-500 mb-5">Rate + operation-wise labour values.</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {LABOUR_FIELDS.map(([k, lbl, kind]) => (
                <div key={k}>
                  <label className="text-[12px] font-semibold text-slate-600 uppercase tracking-wide">{lbl}</label>
                  <input
                    type={kind === "text" ? "text" : "number"}
                    disabled={!canEditProd}
                    value={production[k] ?? (kind === "text" ? "" : 0)}
                    onChange={(ev) => setProduction((p) => ({ ...p, [k]: kind === "text" ? ev.target.value : Number(ev.target.value) }))}
                    data-testid={`prod-${k}`}
                    placeholder={kind === "text" ? "Describe any additional work" : undefined}
                    className="mt-1.5 w-full h-10 px-3 rounded-md border border-slate-200 bg-white text-sm font-mono outline-none focus:border-brand focus:ring-2 focus:ring-rose-100"
                  />
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>
        )}

        {/* ------------- Attachments ------------- */}
        <TabsContent value="attachments">
          <Card className="border-slate-200 rounded-md bg-white overflow-hidden">
            <div className="p-4 flex items-center justify-between border-b border-slate-100">
              <div>
                <div className="font-heading font-semibold text-[15px]">Attachments</div>
                <div className="text-[12px] text-slate-500">
                  Buyer tech-packs, purchase orders, artwork.
                  {canManageAttachments ? " Merchandiser & Admin can upload/delete. All roles can view." : " View & download only — Merchandiser/Admin can add."}
                </div>
              </div>
              {canManageAttachments && (
                <label className={`inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium cursor-pointer ${attBusy ? "bg-slate-200 text-slate-500" : "bg-brand hover:bg-brand-dark text-white"}`} data-testid="attachment-upload-label">
                  <Upload className="h-4 w-4" /> {attBusy ? "Uploading…" : "Upload file"}
                  <input
                    type="file"
                    className="hidden"
                    disabled={attBusy}
                    onChange={(ev) => { const f = ev.target.files?.[0]; if (f) { uploadAttachment(f); ev.target.value = ""; } }}
                    data-testid="attachment-file-input"
                  />
                </label>
              )}
            </div>
            <div className="overflow-x-auto">
              <table className="opsiq-table">
                <thead>
                  <tr>
                    <th>File</th>
                    <th style={{ width: 120 }}>Size</th>
                    <th style={{ width: 180 }}>Uploaded By</th>
                    <th style={{ width: 200 }}>Uploaded At (IST)</th>
                    <th style={{ width: 160 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {(e.attachments || []).map((a) => (
                    <tr key={a.id} data-testid={`attachment-row-${a.id}`}>
                      <td className="font-semibold text-slate-800 inline-flex items-center gap-2"><Paperclip className="h-4 w-4 text-slate-400" /> {a.name}</td>
                      <td className="font-mono text-[12.5px] text-slate-600">{(a.size / 1024).toFixed(1)} KB</td>
                      <td className="text-[12.5px] text-slate-600">{a.uploaded_by}</td>
                      <td className="font-mono text-[12.5px] text-slate-600">{new Date(a.uploaded_at).toLocaleString("en-GB", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false })}</td>
                      <td className="text-right">
                        <a
                          href={`${process.env.REACT_APP_BACKEND_URL}/api/enquiries/${id}/attachments/${a.id}`}
                          target="_blank" rel="noreferrer"
                          className="inline-flex items-center gap-1 px-2 py-1 rounded text-brand hover:bg-brand-soft text-[12.5px] font-medium"
                          data-testid={`attachment-download-${a.id}`}
                        >
                          <Download className="h-3.5 w-3.5" /> Download
                        </a>
                        {canManageAttachments && (
                          <button
                            onClick={() => deleteAttachment(a.id)}
                            className="ml-2 inline-flex items-center gap-1 px-2 py-1 rounded text-slate-400 hover:text-red-600 text-[12.5px]"
                            data-testid={`attachment-delete-${a.id}`}
                            aria-label="Delete attachment"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {(!e.attachments || e.attachments.length === 0) && (
                    <tr><td colSpan={5} className="text-center py-8 text-slate-500 text-[13px]">No attachments yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>

        {/* ------------- Version History ------------- */}
        <TabsContent value="versions">
          <Card className="border-slate-200 rounded-md bg-white overflow-hidden">
            <div className="p-4 flex items-center justify-between border-b border-slate-100">
              <div>
                <div className="font-heading font-semibold text-[15px]">Version History</div>
                <div className="text-[12px] text-slate-500">Each snapshot captures the fabric, tapes/elastic and production data at that moment.</div>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={openCompare} disabled={versions.length === 0} data-testid="enquiry-compare-btn">
                  <Layers className="h-4 w-4 mr-1" /> Compare
                </Button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="opsiq-table">
                <thead>
                  <tr>
                    <th style={{ width: 80 }}>Version</th>
                    <th style={{ width: 180 }}>Saved</th>
                    <th style={{ width: 200 }}>By</th>
                    <th>Note</th>
                    <th style={{ width: 90 }}>Fabrics</th>
                    <th style={{ width: 90 }}>Tapes</th>
                    <th className="text-right" style={{ width: 240 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="bg-brand-soft/40">
                    <td className="font-mono font-bold text-brand">v{e.version || 1}</td>
                    <td className="text-[12.5px] text-slate-600">{(e.updated_at || "").slice(0, 16).replace("T", " ")}</td>
                    <td className="text-[12.5px] text-slate-600">{e.updated_by || "—"}</td>
                    <td className="text-[12.5px] text-slate-500 italic">Current (live)</td>
                    <td className="font-mono text-[12.5px]">{fabrics.length}</td>
                    <td className="font-mono text-[12.5px]">{tapes.length}</td>
                    <td></td>
                  </tr>
                  {[...versions].sort((a, b) => (b.version || 0) - (a.version || 0)).map((v) => {
                    const isOpen = expandedVersion === v.version;
                    // Diff target = "current" (live edit state) or "previous" (v-1 in versions[])
                    const liveSnap = { version: e.version, fabrics, tapes_elastic: tapes, production_data: production };
                    const prevSnap = versions.find((x) => (x.version || 0) === (v.version || 0) - 1);
                    const target = diffAgainst === "current" ? liveSnap : prevSnap;
                    return (
                      <>
                        <tr key={v.version} data-testid={`enquiry-version-${v.version}`} className={isOpen ? "bg-brand-soft/20" : ""}>
                          <td className="font-mono font-bold">v{v.version}</td>
                          <td className="text-[12.5px] text-slate-600">{(v.created_at || "").slice(0, 16).replace("T", " ")}</td>
                          <td className="text-[12.5px] text-slate-600">{v.created_by || "—"}</td>
                          <td className="text-[12.5px] text-slate-700">{v.note || "—"}</td>
                          <td className="font-mono text-[12.5px]">{(v.fabrics || []).length}</td>
                          <td className="font-mono text-[12.5px]">{(v.tapes_elastic || []).length}</td>
                          <td className="text-right">
                            <div className="inline-flex items-center gap-1">
                              <Button size="sm" variant="outline" onClick={() => setExpandedVersion(isOpen ? null : v.version)} data-testid={`view-diff-v${v.version}`}>
                                {isOpen ? <ChevronDown className="h-3.5 w-3.5 mr-1" /> : <ChevronRight className="h-3.5 w-3.5 mr-1" />}
                                {isOpen ? "Hide diff" : "View diff"}
                              </Button>
                              {canEdit && (
                                <Button size="sm" variant="outline" onClick={() => restoreVersion(v.version)} data-testid={`restore-v${v.version}`}>
                                  <RotateCcw className="h-3.5 w-3.5 mr-1" /> Restore
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                        {isOpen && (
                          <tr data-testid={`enquiry-version-diff-${v.version}`}>
                            <td colSpan={7} className="p-0">
                              <VersionDiffView
                                snapshot={v}
                                target={target}
                                targetLabel={diffAgainst === "current" ? `Current (v${e.version || 1})` : prevSnap ? `v${prevSnap.version}` : "No previous version"}
                                diffAgainst={diffAgainst}
                                setDiffAgainst={setDiffAgainst}
                                canSeeProdData={canSeeProdData}
                                hasPrev={!!prevSnap}
                              />
                            </td>
                          </tr>
                        )}
                      </>
                    );
                  })}
                  {versions.length === 0 && (
                    <tr>
                      <td colSpan={7} className="text-center py-8 text-slate-500 text-[13px]">
                        No older snapshots yet. Click <span className="font-semibold">"New Version"</span> in the header to save the current state.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="activity">
          <Card className="p-6 border-slate-200 rounded-md bg-white">
            <div className="font-heading font-semibold text-[15px] mb-4">Activity Log</div>
            <ol className="space-y-3">
              {(e.activity_log || []).map((a, i) => (
                <li key={i} className="flex gap-3 items-start">
                  <div className="h-2 w-2 rounded-full bg-brand mt-2" />
                  <div className="flex-1">
                    <div className="text-[13.5px]"><span className="font-semibold">{a.action}</span> {a.comment && <span className="text-slate-600">— {a.comment}</span>}</div>
                    <div className="text-[11.5px] text-slate-500 mt-0.5">{a.by} · {a.at}</div>
                  </div>
                </li>
              ))}
              {(!e.activity_log || e.activity_log.length === 0) && <div className="text-sm text-slate-500">No activity yet.</div>}
            </ol>
          </Card>
        </TabsContent>
      </Tabs>

      {/* New Version dialog */}
      <Dialog open={newVersionOpen} onOpenChange={setNewVersionOpen}>
        <DialogContent data-testid="new-version-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><GitCommit className="h-5 w-5 text-brand" /> Save New Version</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-[12.5px] text-slate-500">The current fabric, tapes/elastic and production data will be snapshotted as v{(e.version || 1) + 1}. You can restore it any time from the Version History tab.</p>
            <Textarea rows={3} value={newVersionNote} onChange={(ev) => setNewVersionNote(ev.target.value)} placeholder="Optional note (e.g. Post-fitting revision)" data-testid="version-note" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewVersionOpen(false)}>Cancel</Button>
            <Button onClick={doNewVersion} className="bg-brand hover:bg-brand-dark text-white" data-testid="version-save-btn">
              <Save className="h-4 w-4 mr-2" /> Save snapshot
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Revise Details dialog */}
      <Dialog open={reviseOpen} onOpenChange={setReviseOpen}>
        <DialogContent data-testid="revise-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><PenSquare className="h-5 w-5 text-amber-700" /> Revise Enquiry Details</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-[12.5px] text-amber-900 flex gap-2">
              <ShieldAlert className="h-4 w-4 mt-0.5 shrink-0" />
              <div>
                Recording a revision will snapshot the current fabric, tapes/elastic and production data as v{e.version || 1}, bump the enquiry to v{(e.version || 1) + 1}, reset status to <b>Pending CAD Input</b>, and send a notification to CAD / Merchandiser / Production / Management. <span className="italic">Email delivery is mocked in the current build.</span>
              </div>
            </div>
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Reason for revision <span className="text-rose-600">*</span></label>
              <Textarea rows={3} value={reviseReason} onChange={(ev) => setReviseReason(ev.target.value)} placeholder="e.g. Buyer changed lining spec — Padding: 80 GSM → 120 GSM" data-testid="revise-reason" className="mt-1" />
            </div>
            {revNotifs?.length > 0 && (
              <div className="rounded-md border border-slate-200 p-3 max-h-40 overflow-y-auto">
                <div className="text-[10.5px] font-mono uppercase tracking-wider text-slate-500 mb-1.5">Past revisions</div>
                <ul className="space-y-1.5">
                  {revNotifs.map((n) => (
                    <li key={n.id} className="text-[12px] text-slate-700 leading-relaxed">
                      <span className="font-semibold">v{n.from_version} → v{n.to_version}</span> · <span className="text-slate-500">{new Date(n.sent_at).toLocaleString("en-GB", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: false })} IST</span> · by {n.revised_by}
                      <br /><span className="text-slate-500 italic">{n.reason}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReviseOpen(false)}>Cancel</Button>
            <Button onClick={doRevise} disabled={reviseBusy} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="revise-confirm-btn">
              <PenSquare className="h-4 w-4 mr-2" /> {reviseBusy ? "Recording…" : "Record revision & notify"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Compare dialog */}
      <Dialog open={compareOpen} onOpenChange={setCompareOpen}>
        <DialogContent className="max-w-5xl" data-testid="compare-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Layers className="h-5 w-5 text-brand" /> Compare versions</DialogTitle>
          </DialogHeader>
          {compareA && compareB && (
            <div className="grid grid-cols-2 gap-4">
              {[[compareA, "A"], [compareB, "B"]].map(([snap, side]) => (
                <div key={side} className="rounded-md border border-slate-200 p-3">
                  <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-2">
                    v{snap.version} <span className="text-slate-400 font-normal">· {snap.note || "—"}</span>
                  </div>
                  <div className="text-[12px] text-slate-600 mb-3">Saved {(snap.created_at || "").slice(0, 16).replace("T", " ")} by {snap.created_by || "—"}</div>
                  <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-2">
                    <VersionBlock title="Fabrics" rows={snap.fabrics || []} cols={["type", "position", "fabric_type", "width", "uom", "consumption", "remark"]} />
                    <VersionBlock title="Tapes & Elastic" rows={snap.tapes_elastic || []} cols={["item", "uom", "width", "consumption", "remark"]} />
                    {canSeeProdData && <VersionBlock title="Sewing Threads" rows={snap.production_data?.sewing_threads || []} cols={["color", "tex_details", "consumption_m"]} />}
                    {canSeeProdData && <VersionBlock title="Embroidery Threads" rows={snap.production_data?.embroidery_threads || []} cols={["color", "tex_details", "consumption_m"]} />}
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------- Small helpers ----------
function Cell({ value, onChange, disabled, testid, placeholder }) {
  return (
    <input
      type="text"
      value={value ?? ""}
      disabled={disabled}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      className="w-full h-8 px-2 rounded border border-transparent hover:border-slate-200 focus:border-brand focus:ring-1 focus:ring-rose-100 bg-transparent focus:bg-white text-[13px] outline-none disabled:cursor-not-allowed"
    />
  );
}
function Select({ value, onChange, options, disabled, testid, placeholder }) {
  return (
    <select
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      data-testid={testid}
      className="w-full h-8 px-2 rounded border border-slate-200 bg-white text-[13px] outline-none focus:border-brand focus:ring-1 focus:ring-rose-100 disabled:cursor-not-allowed disabled:bg-slate-50"
    >
      {placeholder && <option value="">{placeholder}</option>}
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}
function VersionBlock({ title, rows, cols }) {
  return (
    <div>
      <div className="text-[11px] font-mono uppercase tracking-wider text-slate-500 mb-1">{title} · {rows.length}</div>
      {rows.length === 0 ? (
        <div className="text-[12px] text-slate-400 italic">None</div>
      ) : (
        <div className="rounded border border-slate-200 overflow-hidden">
          <table className="w-full text-[11.5px]">
            <thead>
              <tr className="bg-slate-50">
                {cols.map((c) => <th key={c} className="text-left px-2 py-1 font-semibold text-slate-600 capitalize">{c.replace(/_/g, " ")}</th>)}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className="border-t border-slate-100">
                  {cols.map((c) => <td key={c} className="px-2 py-1 text-slate-700">{String(r[c] ?? "")}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ---------- Version diff view (inline expansion in Version History) ----------
// Compares a historical snapshot against either the live/current data or the previous
// snapshot (v-1). Uses the classic git-diff palette:
//   yellow  = value in an existing row changed between snapshot and target
//   green   = row/field added in target (only exists there)
//   red     = row/field removed in target (only exists in this snapshot)

function _cellsEqual(a, b) {
  // Treat "", null, undefined and 0-string-vs-number as equal to reduce diff noise.
  const na = a === null || a === undefined ? "" : String(a).trim();
  const nb = b === null || b === undefined ? "" : String(b).trim();
  if (na === nb) return true;
  const fa = parseFloat(na); const fb = parseFloat(nb);
  if (!Number.isNaN(fa) && !Number.isNaN(fb) && fa === fb) return true;
  return false;
}

function DiffTable({ title, cols, snapRows = [], targetRows = [] }) {
  // Row alignment strategy: match by index. Longer array's tail rows are added/removed.
  const maxLen = Math.max(snapRows.length, targetRows.length);
  // If both empty, don't render
  if (maxLen === 0) return (
    <div>
      <div className="text-[11px] font-mono uppercase tracking-wider text-slate-500 mb-1">{title} · 0</div>
      <div className="text-[12px] text-slate-400 italic">Empty in both.</div>
    </div>
  );
  return (
    <div>
      <div className="text-[11px] font-mono uppercase tracking-wider text-slate-500 mb-1">{title} · {snapRows.length} / {targetRows.length}</div>
      <div className="rounded border border-slate-200 overflow-hidden">
        <table className="w-full text-[11.5px]">
          <thead>
            <tr className="bg-slate-50">
              <th className="text-left px-2 py-1 font-semibold text-slate-500 w-8">#</th>
              {cols.map((c) => <th key={c} className="text-left px-2 py-1 font-semibold text-slate-600 capitalize">{c.replace(/_/g, " ")}</th>)}
              <th className="text-right px-2 py-1 font-semibold text-slate-500 w-16">Status</th>
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: maxLen }).map((_, i) => {
              const s = snapRows[i];
              const t = targetRows[i];
              const removed = s !== undefined && t === undefined;    // present in this snapshot, missing in target
              const added   = s === undefined && t !== undefined;    // missing here, present in target
              const rowClass =
                removed ? "bg-red-50" :
                added   ? "bg-emerald-50" :
                "border-t border-slate-100";
              const statusBadge = removed
                ? <span className="text-[10px] font-semibold text-red-700">REMOVED</span>
                : added
                ? <span className="text-[10px] font-semibold text-emerald-700">ADDED</span>
                : <span className="text-[10px] font-mono text-slate-400">—</span>;
              return (
                <tr key={i} className={`border-t border-slate-100 ${rowClass}`} data-testid={`diff-row-${title}-${i}`}>
                  <td className="px-2 py-1 text-slate-400 font-mono">{i + 1}</td>
                  {cols.map((c) => {
                    // The value we render is the SNAPSHOT'S value (that's what the reviewer is inspecting)
                    // — unless the row was added (only in target), in which case we render the target's value with green.
                    if (added) {
                      return <td key={c} className="px-2 py-1 text-emerald-800 line-through-none"><b>{String(t?.[c] ?? "")}</b></td>;
                    }
                    if (removed) {
                      return <td key={c} className="px-2 py-1 text-red-800 line-through">{String(s?.[c] ?? "")}</td>;
                    }
                    const changed = !_cellsEqual(s?.[c], t?.[c]);
                    return (
                      <td key={c} className={`px-2 py-1 ${changed ? "bg-yellow-100 text-yellow-900 font-semibold" : "text-slate-700"}`} title={changed ? `Target: ${String(t?.[c] ?? "—")}` : undefined}>
                        {String(s?.[c] ?? "")}
                        {changed && (
                          <span className="ml-1 text-[10px] font-mono text-yellow-700">← {String(t?.[c] ?? "—")}</span>
                        )}
                      </td>
                    );
                  })}
                  <td className="px-2 py-1 text-right">{statusBadge}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function DiffScalarBlock({ title, snap, target, fields }) {
  return (
    <div>
      <div className="text-[11px] font-mono uppercase tracking-wider text-slate-500 mb-1">{title}</div>
      <div className="rounded border border-slate-200 overflow-hidden">
        <table className="w-full text-[11.5px]">
          <tbody>
            {fields.map(([k, label]) => {
              const sv = snap?.[k];
              const tv = target?.[k];
              const changed = !_cellsEqual(sv, tv);
              return (
                <tr key={k} className={`border-t border-slate-100 ${changed ? "bg-yellow-50" : ""}`} data-testid={`diff-scalar-${title}-${k}`}>
                  <td className="px-2 py-1 text-slate-500 w-64">{label}</td>
                  <td className={`px-2 py-1 ${changed ? "text-yellow-900 font-semibold" : "text-slate-700"}`}>
                    {String(sv ?? "—")}
                    {changed && <span className="ml-2 text-[10px] font-mono text-yellow-700">← {String(tv ?? "—")}</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function VersionDiffView({ snapshot, target, targetLabel, diffAgainst, setDiffAgainst, canSeeProdData, hasPrev }) {
  // Summary counts
  const summary = useMemo(() => {
    let changed = 0, added = 0, removed = 0;
    if (!target) return { changed: 0, added: 0, removed: 0 };
    const arrayCompare = (a = [], b = [], keys) => {
      const max = Math.max(a.length, b.length);
      for (let i = 0; i < max; i++) {
        const sa = a[i], sb = b[i];
        if (sa && !sb) removed++;
        else if (!sa && sb) added++;
        else if (sa && sb) {
          for (const k of keys) if (!_cellsEqual(sa[k], sb[k])) changed++;
        }
      }
    };
    arrayCompare(snapshot.fabrics || [], target.fabrics || [], ["type","position","fabric_type","width","uom","consumption","remark"]);
    arrayCompare(snapshot.tapes_elastic || [], target.tapes_elastic || [], ["item","uom","width","consumption","remark"]);
    if (canSeeProdData) {
      arrayCompare(snapshot.production_data?.sewing_threads || [], target.production_data?.sewing_threads || [], ["color","tex_details","consumption_m"]);
      arrayCompare(snapshot.production_data?.embroidery_threads || [], target.production_data?.embroidery_threads || [], ["color","tex_details","consumption_m"]);
      const scalarKeys = ["labour_rate","bartack","button_hole","eye_hole","button_stitch","snap","folding_ironing","any_other_work"];
      for (const k of scalarKeys) if (!_cellsEqual(snapshot.production_data?.[k], target.production_data?.[k])) changed++;
    }
    return { changed, added, removed };
  }, [snapshot, target, canSeeProdData]);

  return (
    <div className="p-4 bg-slate-50/60 border-t-2 border-brand/30" data-testid="version-diff-view">
      {/* Controls row */}
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <div className="text-[13px] font-heading font-semibold flex items-center gap-2">
          <GitCompare className="h-4 w-4 text-brand" />
          v{snapshot.version} → {targetLabel}
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="inline-flex items-center gap-1 text-[11px]" data-testid="diff-summary">
            <span className="px-1.5 py-0.5 rounded bg-yellow-100 text-yellow-900 font-mono font-semibold">{summary.changed} changed</span>
            <span className="px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 font-mono font-semibold">{summary.added} added</span>
            <span className="px-1.5 py-0.5 rounded bg-red-100 text-red-800 font-mono font-semibold">{summary.removed} removed</span>
          </div>
          <div className="inline-flex items-center gap-1 text-[11px]">
            <span className="uppercase tracking-widest text-slate-500 mr-1">Compare to:</span>
            <button
              onClick={() => setDiffAgainst("current")}
              className={`px-2 py-0.5 rounded border text-[11.5px] font-semibold ${diffAgainst === "current" ? "bg-brand text-white border-brand" : "bg-white text-slate-700 border-slate-200 hover:border-brand/40"}`}
              data-testid="diff-target-current"
            >Current</button>
            <button
              onClick={() => setDiffAgainst("previous")}
              disabled={!hasPrev}
              className={`px-2 py-0.5 rounded border text-[11.5px] font-semibold ${diffAgainst === "previous" ? "bg-brand text-white border-brand" : "bg-white text-slate-700 border-slate-200 hover:border-brand/40 disabled:opacity-40 disabled:cursor-not-allowed"}`}
              data-testid="diff-target-previous"
            >Previous version</button>
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="text-[10.5px] font-mono uppercase tracking-widest text-slate-500 flex items-center gap-3 mb-3">
        <span className="inline-flex items-center gap-1"><span className="inline-block h-3 w-3 rounded bg-yellow-200 border border-yellow-300"></span> changed</span>
        <span className="inline-flex items-center gap-1"><span className="inline-block h-3 w-3 rounded bg-emerald-200 border border-emerald-300"></span> added</span>
        <span className="inline-flex items-center gap-1"><span className="inline-block h-3 w-3 rounded bg-red-200 border border-red-300"></span> removed</span>
        <span className="text-slate-400">· value shown is v{snapshot.version} · <b>←</b> points to target's value</span>
      </div>

      {!target ? (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-[12.5px] text-amber-900">
          No previous version exists to compare against. Switch to <b>Current</b>.
        </div>
      ) : (
        <div className="space-y-4">
          <DiffTable title="Fabrics" cols={["type","position","fabric_type","width","uom","consumption","remark"]} snapRows={snapshot.fabrics || []} targetRows={target.fabrics || []} />
          <DiffTable title="Tapes & Elastic" cols={["item","uom","width","consumption","remark"]} snapRows={snapshot.tapes_elastic || []} targetRows={target.tapes_elastic || []} />
          {canSeeProdData && (
            <>
              <DiffTable title="Sewing Threads" cols={["color","tex_details","consumption_m"]} snapRows={snapshot.production_data?.sewing_threads || []} targetRows={target.production_data?.sewing_threads || []} />
              <DiffTable title="Embroidery Threads" cols={["color","tex_details","consumption_m"]} snapRows={snapshot.production_data?.embroidery_threads || []} targetRows={target.production_data?.embroidery_threads || []} />
              <DiffScalarBlock
                title="Labour & Finishing"
                snap={snapshot.production_data}
                target={target.production_data}
                fields={[
                  ["labour_rate", "Labour Rate"],
                  ["bartack", "Bartack (No. of Points)"],
                  ["button_hole", "Button Hole"],
                  ["eye_hole", "Eye Hole"],
                  ["button_stitch", "Button Stitch"],
                  ["snap", "Snap / Riveting"],
                  ["folding_ironing", "Folding & Ironing"],
                  ["any_other_work", "Any Other Work"],
                ]}
              />
            </>
          )}
        </div>
      )}
    </div>
  );
}
